import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { navItems } from '../data/mock';
import SignInWidget from './SignInWidget';

const Logo = () => (
  <Link to="/" className="flex items-center group" aria-label="Emerald Corporates — home">
    <img
      src="/emerald-logo.png"
      alt="Emerald Corporates"
      width={168}
      height={112}
      className="h-14 w-auto object-contain transition-opacity group-hover:opacity-90"
      data-testid="site-logo"
    />
  </Link>
);

const Header = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(null);

  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 bg-[#e2ede6]/95 backdrop-blur-sm border-b border-[#0f5c3d]/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <Logo />

        {/* Desktop right side: nav + sign-in */}
        <div className="hidden lg:flex items-center gap-3">
          <nav className="flex items-center gap-1">
            {navItems.map((item) => {
              const active = location.pathname === item.to;
              if (item.submenu) {
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(item.label)}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <Link
                      to={item.to}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-1 px-4 py-2 text-[15px] font-medium transition-colors ${
                        active
                          ? 'text-[#0f5c3d]'
                          : 'text-[#0b1a2e] hover:text-[#0f5c3d]'
                      }`}
                    >
                      {item.label}
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${
                          openMenu === item.label ? 'rotate-180' : ''
                        }`}
                      />
                    </Link>
                    {openMenu === item.label && (
                      <div className="absolute left-0 top-full pt-2 min-w-[280px]">
                        <div className="bg-white rounded-md shadow-xl border border-black/5 py-2 overflow-hidden max-h-[70vh] overflow-y-auto">
                          {item.submenu.map((sub) => (
                            <Link
                              key={sub.label}
                              to={sub.to}
                              className="block px-4 py-2.5 text-sm text-[#0b1a2e] hover:bg-[#e2ede6] hover:text-[#0f5c3d] transition-colors"
                            >
                              {sub.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`px-4 py-2 text-[15px] font-medium transition-colors ${
                    active
                      ? 'text-[#0f5c3d]'
                      : 'text-[#0b1a2e] hover:text-[#0f5c3d]'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <SignInWidget variant="icon" />
        </div>

        {/* Mobile actions */}
        <div className="lg:hidden flex items-center gap-2">
          <SignInWidget variant="icon" />
          <button
            className="p-2 text-[#0b1a2e]"
            onClick={() => setOpen(!open)}
            aria-label="toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {open && (
        <div className="lg:hidden border-t border-[#0f5c3d]/10 bg-[#e2ede6]">
          <nav className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-[#0f5c3d]/10 last:border-0">
                {item.submenu ? (
                  <>
                    <div className="flex items-center justify-between border-b border-transparent">
                      <Link
                        to={item.to}
                        className="flex-1 py-3 text-[#0b1a2e] font-medium"
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() =>
                          setMobileOpen(mobileOpen === item.label ? null : item.label)
                        }
                        className="p-3 text-[#0b1a2e]"
                        aria-label={`toggle ${item.label} submenu`}
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            mobileOpen === item.label ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    </div>
                    {mobileOpen === item.label && (
                      <div className="pb-3 pl-4 space-y-2">
                        {item.submenu.map((s) => (
                          <Link
                            key={s.label}
                            to={s.to}
                            className="block text-sm text-[#0b1a2e]/80 hover:text-[#0f5c3d]"
                          >
                            {s.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.to}
                    className="block py-3 text-[#0b1a2e] font-medium"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
